import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, IconButton, Paper, Card, CardActionArea,
  CardContent, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ApartmentIcon from '@mui/icons-material/Apartment';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

// ── Küçük yardımcılar ──────────────────────────────────────────────
const fileType = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (['doc', 'docx'].includes(ext)) return 'Word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'Excel';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'Resim';
  return ext ? ext.toUpperCase() : '—';
};
const fmtTL = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return (y && m && d) ? `${d}.${m}.${y}` : s;
};
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '—');
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdminTgbTracking() {
  const [teknokents, setTeknokents] = useState([]);
  const [selected, setSelected] = useState(null); // seçili teknokent objesi
  const [companies, setCompanies] = useState([]);

  // Liste: yeni teknokent ekleme
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  // Detay verileri
  const [documents, setDocuments] = useState([]);
  const [fees, setFees] = useState([]);
  const [feeFiles, setFeeFiles] = useState([]);

  // Manuel ücret giriş formu
  const blankFee = { company_id: '', fee_date: todayISO(), description: '', amount: '' };
  const [feeForm, setFeeForm] = useState(blankFee);

  // Ücret belgesi yükleme: firma seçimi
  const [feeFileCompany, setFeeFileCompany] = useState('');

  const docInputRef = useRef(null);
  const feeFileInputRef = useRef(null);

  // ── Yüklemeler ───────────────────────────────────────────────────
  const fetchTeknokents = useCallback(() => {
    axios.get(`${API_BASE}/teknokents`)
      .then((res) => setTeknokents(res.data || []))
      .catch((err) => console.error('TGB listesi alınamadı:', err));
  }, []);

  useEffect(() => {
    fetchTeknokents();
    axios.get(`${API_BASE}/companies`)
      .then((res) => setCompanies((res.data || []).filter((c) => c.role !== 'Admin')))
      .catch((err) => console.error('Firma listesi alınamadı:', err));
  }, [fetchTeknokents]);

  const loadDetail = useCallback((id) => {
    axios.get(`${API_BASE}/teknokents/${id}/documents`).then((r) => setDocuments(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/teknokents/${id}/fees`).then((r) => setFees(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/teknokents/${id}/fee-files`).then((r) => setFeeFiles(r.data || [])).catch(() => {});
  }, []);

  const openTeknokent = (t) => {
    setSelected(t);
    setFeeForm(blankFee);
    setFeeFileCompany('');
    loadDetail(t.id);
  };

  const backToList = () => {
    setSelected(null);
    fetchTeknokents(); // sayaçları tazele
  };

  // ── Teknokent ekle / sil ─────────────────────────────────────────
  const submitAdd = () => {
    const name = newName.trim();
    if (!name) return;
    axios.post(`${API_BASE}/teknokents`, { name })
      .then(() => { setAddOpen(false); setNewName(''); fetchTeknokents(); })
      .catch((err) => console.error('Teknokent eklenemedi:', err));
  };

  const deleteTeknokent = (t) => {
    if (!window.confirm(`"${t.name}" teknokentini ve tüm belge/ücret kayıtlarını silmek istediğinize emin misiniz?`)) return;
    axios.delete(`${API_BASE}/teknokents/${t.id}`)
      .then(() => { backToList(); })
      .catch((err) => console.error('Teknokent silinemedi:', err));
  };

  // ── Döküman yükle / sil / indir ──────────────────────────────────
  const onDocPicked = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append('file', file);
    axios.post(`${API_BASE}/teknokents/${selected.id}/documents`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => loadDetail(selected.id))
      .catch((err) => console.error('Döküman yüklenemedi:', err));
  };

  const deleteDocument = (id) => {
    if (!window.confirm('Bu dökümanı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/teknokents/documents/${id}`)
      .then(() => setDocuments((prev) => prev.filter((d) => d.id !== id)))
      .catch((err) => console.error('Döküman silinemedi:', err));
  };

  // ── Ücret kaydı ekle / sil ───────────────────────────────────────
  const submitFee = () => {
    if (!selected) return;
    if (!feeForm.amount && feeForm.amount !== 0) return;
    axios.post(`${API_BASE}/teknokents/${selected.id}/fees`, {
      company_id: feeForm.company_id || null,
      fee_date: feeForm.fee_date || null,
      description: feeForm.description || null,
      amount: feeForm.amount,
    })
      .then(() => { setFeeForm({ ...blankFee, fee_date: feeForm.fee_date }); loadDetail(selected.id); })
      .catch((err) => console.error('Ücret kaydı eklenemedi:', err));
  };

  const deleteFee = (id) => {
    if (!window.confirm('Bu ücret kaydını silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/teknokents/fees/${id}`)
      .then(() => setFees((prev) => prev.filter((f) => f.id !== id)))
      .catch((err) => console.error('Ücret kaydı silinemedi:', err));
  };

  // ── Ücret belgesi yükle / sil ────────────────────────────────────
  const onFeeFilePicked = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append('file', file);
    if (feeFileCompany) fd.append('company_id', feeFileCompany);
    axios.post(`${API_BASE}/teknokents/${selected.id}/fee-files`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => loadDetail(selected.id))
      .catch((err) => console.error('Ücret belgesi yüklenemedi:', err));
  };

  const deleteFeeFile = (id) => {
    if (!window.confirm('Bu belgeyi silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/teknokents/fee-files/${id}`)
      .then(() => setFeeFiles((prev) => prev.filter((f) => f.id !== id)))
      .catch((err) => console.error('Belge silinemedi:', err));
  };

  // İndirme uçları admin-token ister; düz <a> bu header'ı taşımaz, bu yüzden
  // axios ile blob olarak çekip (global x-admin-token ile) kaydettiriyoruz.
  const download = async (url, fileName) => {
    try {
      const { data } = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('İndirme hatası:', err);
    }
  };

  // ── LİSTE GÖRÜNÜMÜ ───────────────────────────────────────────────
  if (!selected) {
    return (
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>TGB Takip</Typography>
          <Typography variant="body2" color="text.secondary">Toplam: {teknokents.length} teknokent</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setNewName(''); setAddOpen(true); }}>
            Yeni Teknokent
          </Button>
        </Stack>

        {teknokents.length === 0 ? (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <ApartmentIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography color="text.secondary" sx={{ mt: 1 }}>Henüz teknokent eklenmemiş.</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
            {teknokents.map((t) => (
              <Card key={t.id} sx={{ borderLeft: 5, borderLeftColor: 'primary.main' }}>
                <CardActionArea onClick={() => openTeknokent(t)}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ApartmentIcon color="primary" />
                      <Typography variant="h6" sx={{ flex: 1, wordBreak: 'break-word' }}>{t.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
                      <Chip size="small" variant="outlined" label={`${t.document_count || 0} döküman`} />
                      <Chip size="small" variant="outlined" label={`${t.fee_count || 0} ücret`} />
                      <Chip size="small" variant="outlined" label={`${t.fee_file_count || 0} belge`} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Yeni Teknokent</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Teknokent İsmi"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
              fullWidth
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)}>İptal</Button>
            <Button onClick={submitAdd} variant="contained" disabled={!newName.trim()}>Kaydet</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── DETAY GÖRÜNÜMÜ ───────────────────────────────────────────────
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={backToList}><ArrowBackIcon /></IconButton>
        <ApartmentIcon color="primary" />
        <Typography variant="h4" sx={{ flexGrow: 1, wordBreak: 'break-word' }}>{selected.name}</Typography>
        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => deleteTeknokent(selected)}>
          Teknokenti Sil
        </Button>
      </Stack>

      {/* ── Dökümanlar ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1 }}>Dökümanlar</Typography>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => docInputRef.current?.click()}>
            Dosya Yükle
          </Button>
          <input ref={docInputRef} type="file" hidden onChange={onDocPicked} />
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <FileTable
          rows={documents}
          emptyText="Henüz döküman yüklenmemiş."
          onDownload={(f) => download(`${API_BASE}/teknokents/documents/${f.id}/download`, f.file_name)}
          onDelete={(f) => deleteDocument(f.id)}
        />
      </Paper>

      {/* ── Başvuru Ücretleri ── */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Başvuru Ücretleri</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {/* Sol: manuel giriş + kayıtlar */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Ücret Bilgileri (Manuel Giriş)
            </Typography>
            <Stack spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel id="fee-company-label">Firma</InputLabel>
                <Select
                  labelId="fee-company-label"
                  label="Firma"
                  value={feeForm.company_id}
                  onChange={(e) => setFeeForm((f) => ({ ...f, company_id: e.target.value }))}
                >
                  <MenuItem value=""><em>Firma seç (opsiyonel)</em></MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.ID} value={c.ID}>{c.Company_Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small" label="Tarih" type="date" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={feeForm.fee_date}
                  onChange={(e) => setFeeForm((f) => ({ ...f, fee_date: e.target.value }))}
                />
                <TextField
                  size="small" label="Tutar (TL)" type="number" fullWidth
                  inputProps={{ step: '0.01', min: 0 }}
                  value={feeForm.amount}
                  onChange={(e) => setFeeForm((f) => ({ ...f, amount: e.target.value }))}
                />
              </Stack>
              <TextField
                size="small" label="Açıklama" fullWidth multiline maxRows={3}
                placeholder="Örn. Sticker başvurusu"
                value={feeForm.description}
                onChange={(e) => setFeeForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained" startIcon={<AddIcon />} onClick={submitFee}
                  disabled={feeForm.amount === '' || feeForm.amount === null}
                >
                  Ekle
                </Button>
              </Box>
            </Stack>

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fees.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell>{fmtDate(f.fee_date)}</TableCell>
                    <TableCell>{f.company_name || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{f.description || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTL(f.amount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Sil">
                        <IconButton size="small" color="error" onClick={() => deleteFee(f.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {fees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      Henüz ücret kaydı yok.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Sağ: ücret belgeleri */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
                Ücret Belgeleri
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                <InputLabel id="feefile-company-label">Firma</InputLabel>
                <Select
                  labelId="feefile-company-label"
                  label="Firma"
                  value={feeFileCompany}
                  onChange={(e) => setFeeFileCompany(e.target.value)}
                >
                  <MenuItem value=""><em>Firma seç (opsiyonel)</em></MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.ID} value={c.ID}>{c.Company_Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => feeFileInputRef.current?.click()}>
                Dosya Yükle
              </Button>
              <input ref={feeFileInputRef} type="file" hidden onChange={onFeeFilePicked} />
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <FileTable
              rows={feeFiles}
              showCompany
              emptyText="Henüz belge yüklenmemiş."
              onDownload={(f) => download(`${API_BASE}/teknokents/fee-files/${f.id}/download`, f.file_name)}
              onDelete={(f) => deleteFeeFile(f.id)}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// Ortak dosya tablosu (Dökümanlar + Ücret Belgeleri).
function FileTable({ rows, emptyText, onDownload, onDelete, showCompany = false }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Dosya Adı</TableCell>
          {showCompany && <TableCell>Firma</TableCell>}
          <TableCell>Tür</TableCell>
          <TableCell>Yükleme Tarihi</TableCell>
          <TableCell align="right">İşlem</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((f) => (
          <TableRow key={f.id} hover>
            <TableCell sx={{ wordBreak: 'break-word' }}>{f.file_name}</TableCell>
            {showCompany && <TableCell>{f.company_name || '—'}</TableCell>}
            <TableCell><Chip size="small" label={fileType(f.file_name)} /></TableCell>
            <TableCell>{fmtDateTime(f.uploaded_at)}</TableCell>
            <TableCell align="right">
              <Tooltip title="İndir">
                <IconButton size="small" color="primary" onClick={() => onDownload(f)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sil">
                <IconButton size="small" color="error" onClick={() => onDelete(f)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={showCompany ? 5 : 4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              {emptyText}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
